import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  fetchWordBank,
  addWordBankEntry,
  requestWordBankDefinition,
  deleteWordBankEntry,
  relearnWordBankWord,
  fetchWordBankVerbFormQuestions,
  fetchWordBankParticleQuestions,
  askSentenceAI,
  fetchWordBankSentences,
  saveWordBankSentence,
  deleteWordBankSentence,
  touchWordBankSentence,
  analyzeWordBankWord,
  searchWords,
  fetchWordDetail,
  synthesizeSpeech,
  isAudioPlaybackAllowed,
  getDailyUsage,
} from "../api";
import VocabularyStep from "./VocabularyStep";
import WordDetail from "./WordDetail";

const POS_OPTIONS = [
  { value: "noun", label: "Noun" },
  { value: "verb", label: "Verb" },
  { value: "particle", label: "Particle" },
];

const STATUS_BADGE = {
  public: null, // public words need no badge
  pending: { label: "Pending review", cls: "bg-dt-gold-bg text-dt-gold-bright" },
  rejected: { label: "Not approved", cls: "bg-parchment-200 text-ink-400" },
};

// Mirror the topic verb list: show the admin-starred display form, else past,
// else the Word's own text — so a verb stored under a non-citation/mis-vocalised
// text still surfaces its intended (e.g. past-tense) form.
const VERB_FORM_KEYS = {
  past: ["past_word", "past"], present: ["present_word", "present"],
  imperative: ["imperative_word", "imperative"], masdar: ["masdar_word", "masdar"],
  ism_faail: ["ism_faail_word", "ism_faail"], ism_mafool: ["ism_mafool_word", "ism_mafool"],
};
function pickVerbForm(s, key) {
  const [wf, tf] = VERB_FORM_KEYS[key] || [];
  if (!wf) return null;
  const t = (s?.[tf] || "").trim();
  if (t) return t;
  return s?.[wf]?.text || null;
}
function verbDisplayText(w) {
  if (!Array.isArray(w.senses)) return w.text;
  for (const s of w.senses) {
    const exId = s?.display_extra_form_id;
    if (exId && Array.isArray(s.extra_forms)) {
      const ex = s.extra_forms.find((e) => e.id === exId);
      if (ex?.text) return ex.text;
    }
  }
  for (const s of w.senses) if (s?.display_form) { const t = pickVerbForm(s, s.display_form); if (t) return t; }
  for (const s of w.senses) { const t = pickVerbForm(s, "past"); if (t) return t; }
  return w.text;
}

/**
 * WordBank — a learner's standalone, per-user word collection.
 *
 * Add words (selecting an existing word from the shared dictionary, or creating
 * a brand-new one that stays private until an admin approves it), browse them
 * grouped by part of speech, and learn them with the same "learn by quizzing"
 * flow used in step 1 of the guided topic journey (VocabularyStep).
 */
export default function WordBank({ onClose, isAdmin = false }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("browse"); // "browse" | "learn" | "sentences"
  const [usage, setUsage] = useState(null); // { count, limit, askCount, askLimit }
  const [verbFormsMap, setVerbFormsMap] = useState({}); // verb_id -> [questions]
  const [particlesMap, setParticlesMap] = useState({}); // verb_id -> [particle questions]

  // "Say it in Arabic" helper + saved sentences
  const [sentences, setSentences] = useState([]);
  const [askText, setAskText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState(null);
  const [savedSentenceKeys, setSavedSentenceKeys] = useState(() => new Set());

  // Tap-a-sentence-word → add to My Words
  const [tokenModal, setTokenModal] = useState(null); // { token } | null
  const [tokenPos, setTokenPos] = useState("noun");
  const [tokenTranslit, setTokenTranslit] = useState("");
  const [tokenDef, setTokenDef] = useState("");
  const [tokenBusy, setTokenBusy] = useState(false);
  const [tokenAiBusy, setTokenAiBusy] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  const [tokenNotice, setTokenNotice] = useState(null); // limit-reached message

  // Add-word form
  const [text, setText] = useState("");
  const [pos, setPos] = useState("noun");
  const [transliteration, setTransliteration] = useState("");
  const [definition, setDefinition] = useState("");
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addMsg, setAddMsg] = useState(null);

  // Word-detail overlay
  const [detailWord, setDetailWord] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const wordAudioRef = useRef(null);

  const limitReached =
    !isAdmin && usage && usage.limit != null && usage.count >= usage.limit;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWordBank();
      setEntries(data.entries || []);
    } catch (e) {
      setError("Could not load your Word Bank.");
    } finally {
      setLoading(false);
    }
    // Verb-form + particle drill questions (parallel; non-blocking for the list).
    fetchWordBankVerbFormQuestions()
      .then((data) => {
        const map = {};
        for (const q of data?.questions || []) {
          (map[q.verb_id] = map[q.verb_id] || []).push(q);
        }
        setVerbFormsMap(map);
      })
      .catch(() => setVerbFormsMap({}));
    fetchWordBankParticleQuestions()
      .then((data) => {
        const map = {};
        for (const q of data?.questions || []) {
          (map[q.verb_id] = map[q.verb_id] || []).push(q);
        }
        setParticlesMap(map);
      })
      .catch(() => setParticlesMap({}));
  }, []);

  const refreshUsage = useCallback(() => {
    getDailyUsage()
      .then((u) =>
        setUsage({
          count: u.word_bank_count || 0,
          limit: u.limits?.max_daily_word_bank_additions,
          askCount: u.sentence_ai_ask_count || 0,
          askLimit: u.limits?.max_daily_sentence_ai_asks,
        })
      )
      .catch(() => {});
  }, []);

  const loadSentences = useCallback(() => {
    fetchWordBankSentences()
      .then((data) => setSentences(data.sentences || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    refreshUsage();
    loadSentences();
  }, [load, refreshUsage, loadSentences]);

  // Debounced search of existing (selectable) words while typing.
  useEffect(() => {
    const q = text.trim();
    if (!q) {
      setMatches([]);
      return;
    }
    let active = true;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const results = await searchWords(q);
        if (active) setMatches((results || []).slice(0, 8));
      } catch {
        if (active) setMatches([]);
      } finally {
        if (active) setSearching(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [text]);

  const bankWordIds = useMemo(
    () => new Set(entries.map((e) => e.word.id)),
    [entries]
  );

  const handleAddExisting = async (w) => {
    setAdding(true);
    setAddError(null);
    setAddMsg(null);
    try {
      const res = await addWordBankEntry({ word_id: w.id });
      setText("");
      setMatches([]);
      setAddMsg(
        res.already_in_bank
          ? `"${w.text}" is already in your Word Bank.`
          : `Added "${w.text}".`
      );
      await load();
      refreshUsage();
    } catch (e) {
      setAddError(e.message || "Could not add that word.");
    } finally {
      setAdding(false);
    }
  };

  const handleAddNew = async () => {
    const t = text.trim();
    if (!t) return;
    setAdding(true);
    setAddError(null);
    setAddMsg(null);
    try {
      const res = await addWordBankEntry({
        text: t,
        pos,
        transliteration: transliteration.trim(),
        definition: definition.trim(),
      });
      setText("");
      setTransliteration("");
      setDefinition("");
      setMatches([]);
      setAddMsg(
        res.already_in_bank
          ? `"${t}" is already in your Word Bank.`
          : `Added "${t}" — it's visible only to you until an admin approves it.`
      );
      await load();
      refreshUsage();
    } catch (e) {
      setAddError(e.message || "Could not add that word.");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (entry) => {
    const prev = entries;
    setEntries((es) => es.filter((e) => e.id !== entry.id));
    try {
      await deleteWordBankEntry(entry.id);
    } catch {
      setEntries(prev); // revert on failure
    }
  };

  const handleRelearn = async (word) => {
    try {
      await relearnWordBankWord(word.id);
      await load(); // refresh known state + ordering
    } catch {
      /* ignore */
    }
  };

  // Ask an admin to supply a definition for a word whose AI look-up wasn't
  // available (e.g. the daily limit was reached), so it never got a meaning.
  const [requestingId, setRequestingId] = useState(null);
  const handleRequestDefinition = async (entry) => {
    setRequestingId(entry.id);
    try {
      await requestWordBankDefinition(entry.id);
      setEntries((es) =>
        es.map((e) =>
          e.id === entry.id
            ? {
                ...e,
                definition_requested: true,
                status: e.status === "public" ? e.status : "pending",
              }
            : e
        )
      );
    } catch {
      /* ignore — the button stays available to retry */
    } finally {
      setRequestingId(null);
    }
  };

  // ── "Say it in Arabic" ────────────────────────────────────────────────────
  const askLimitReached =
    !isAdmin && usage && usage.askLimit != null && usage.askCount >= usage.askLimit;

  const sentenceKey = (s) => `${s.arabic} ${s.translation || ""}`;

  const handleAsk = async () => {
    const t = askText.trim();
    if (!t || asking) return;
    setAsking(true);
    setAskError(null);
    setSuggestions([]);
    try {
      const res = await askSentenceAI(t);
      setSuggestions(res.sentences || []);
      refreshUsage();
    } catch (e) {
      setAskError(e.message || "Could not get suggestions.");
    } finally {
      setAsking(false);
    }
  };

  const handleSaveSentence = async (s) => {
    try {
      await saveWordBankSentence({ arabic: s.arabic, translation: s.translation || "" });
      setSavedSentenceKeys((prev) => new Set(prev).add(sentenceKey(s)));
      loadSentences(); // reflect the new item, recency order, and any LRU eviction
    } catch {
      /* ignore */
    }
  };

  const handlePlaySentence = async (s) => {
    playWord({ text: s.arabic });
    // Bump to the top (most-recently-used) locally + persist.
    setSentences((prev) => [s, ...prev.filter((x) => x.id !== s.id)]);
    try {
      await touchWordBankSentence(s.id);
    } catch {
      /* ignore */
    }
  };

  const handleDeleteSentence = async (sentence) => {
    const prev = sentences;
    setSentences((list) => list.filter((s) => s.id !== sentence.id));
    try {
      await deleteWordBankSentence(sentence.id);
    } catch {
      setSentences(prev); // revert on failure
    }
  };

  // Strip leading/trailing non-Arabic characters (punctuation) from a token.
  const cleanToken = (raw) =>
    (raw || "").replace(/^[^؀-ۿ]+|[^؀-ۿ]+$/g, "");

  const handleTokenClick = (raw) => {
    const token = cleanToken(raw);
    if (!token) return;
    if (limitReached) {
      setTokenNotice(
        `You've reached your daily limit of ${usage.limit} words. Try again tomorrow.`
      );
      return;
    }
    setTokenModal({ token });
    setTokenPos("noun");
    setTokenTranslit("");
    setTokenDef("");
    setTokenError(null);
  };

  const handleTokenAi = async () => {
    if (!tokenModal) return;
    setTokenAiBusy(true);
    setTokenError(null);
    try {
      const r = await analyzeWordBankWord(tokenModal.token);
      if (r.definition) setTokenDef(r.definition);
      if (r.transliteration) setTokenTranslit(r.transliteration);
      if (r.pos) setTokenPos(r.pos);
    } catch (e) {
      setTokenError(e.message || "Could not analyze the word.");
    } finally {
      setTokenAiBusy(false);
    }
  };

  const handleTokenAdd = async () => {
    if (!tokenModal) return;
    setTokenBusy(true);
    setTokenError(null);
    try {
      await addWordBankEntry({
        text: tokenModal.token,
        pos: tokenPos,
        transliteration: tokenTranslit.trim(),
        definition: tokenDef.trim(),
      });
      setTokenModal(null);
      await load();
      refreshUsage();
    } catch (e) {
      if (e.status === 429) {
        setTokenModal(null);
        setTokenNotice(e.message);
      } else {
        setTokenError(e.message || "Could not add the word.");
      }
    } finally {
      setTokenBusy(false);
    }
  };

  const openDetail = useCallback(async (wordId) => {
    setDetailLoading(true);
    setDetailError(null);
    setDetailWord(null);
    try {
      const data = await fetchWordDetail(wordId);
      setDetailWord(data);
    } catch {
      setDetailError("Could not load word details.");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const playWord = useCallback(async (word) => {
    try {
      if (wordAudioRef.current) {
        wordAudioRef.current.pause();
        wordAudioRef.current = null;
      }
      const data = await synthesizeSpeech(word.text, "");
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_content}`);
      wordAudioRef.current = audio;
      audio.onended = () => {
        wordAudioRef.current = null;
      };
      audio.play().catch(() => {});
    } catch {
      /* ignore playback errors */
    }
  }, []);

  // ── Data for the Learn (VocabularyStep) flow ──────────────────────────────
  const knownIds = useMemo(
    () => new Set(entries.filter((e) => e.is_known).map((e) => e.word.id)),
    [entries]
  );
  const reviewMap = useMemo(() => {
    const m = {};
    for (const e of entries) if (e.review_item_id) m[e.word.id] = e.review_item_id;
    return m;
  }, [entries]);

  const sections = useMemo(() => {
    const groups = { noun: [], verb: [], particle: [], other: [] };
    for (const e of entries) {
      const w = { ...e.word, word_type: e.pos === "verb" ? "verb" : "vocab" };
      (groups[e.pos] || groups.other).push(w);
    }
    return [
      { label: "Nouns", items: groups.noun },
      { label: "Verbs", items: groups.verb },
      { label: "Particles", items: groups.particle },
      { label: "Other", items: groups.other },
    ].filter((s) => s.items.length > 0);
  }, [entries]);

  // Map word id → its entry (for delete + status badge in the browse list).
  const entryByWordId = useMemo(() => {
    const m = {};
    for (const e of entries) m[e.word.id] = e;
    return m;
  }, [entries]);

  // Words the user has already learnt — offered for re-learning.
  const learntEntries = useMemo(() => entries.filter((e) => e.is_known), [entries]);

  const meaningOf = (w) =>
    w.senses?.find((s) => s.is_primary)?.definition ||
    w.senses?.[0]?.definition ||
    "";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] p-4"
      onClick={onClose}
    >
      <div
        className="surface-glass dark-scope rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-ink-800">Word Bank</h2>
            <p className="text-xs text-ink-400">
              Your personal collection of words to learn.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            aria-label="Close"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mode toggle */}
        <div className="flex gap-2 px-6 pt-4 shrink-0">
          {[
            { id: "browse", label: "My words" },
            { id: "learn", label: "Learn" },
            { id: "sentences", label: "Sentences" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setMode(t.id)}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                mode === t.id
                  ? "bg-ink-500 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 scroll-elegant">
          {mode === "browse" ? (
            <>
              {/* ── Add-word form ── */}
              <div className="surface-glass mb-5 p-4 rounded-xl">
                <h3 className="text-sm font-semibold text-ink-700 mb-2">Add a word</h3>
                {usage && usage.limit != null && !isAdmin && (
                  <p className="text-xs text-ink-400 mb-2">
                    {Math.max(usage.limit - usage.count, 0)} of {usage.limit} daily
                    additions remaining.
                  </p>
                )}

                <input
                  type="text"
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value);
                    setAddMsg(null);
                    setAddError(null);
                  }}
                  placeholder="Type a word in Arabic…"
                  dir="rtl"
                  disabled={limitReached}
                  className="w-full px-3 py-2 rounded-lg border border-parchment-300 bg-white text-base text-ink-800 placeholder:text-ink-300 focus:outline-none focus:ring-2 focus:ring-ink-400 disabled:opacity-60"
                />

                {/* Existing matches to select */}
                {text.trim() && (
                  <div className="mt-2">
                    {searching && <p className="text-xs text-ink-400">Searching…</p>}
                    {matches.length > 0 && (
                      <>
                        <p className="text-xs text-ink-500 mb-1">
                          Already in the dictionary — tap to add the existing word:
                        </p>
                        <ul className="list-none p-0 border border-parchment-300 rounded-lg overflow-hidden bg-white">
                          {matches.map((w) => {
                            const inBank = bankWordIds.has(w.id);
                            return (
                              <li key={w.id}>
                                <button
                                  disabled={adding || inBank || limitReached}
                                  onClick={() => handleAddExisting(w)}
                                  className="w-full text-left px-3 py-2 text-sm border-b border-parchment-200 last:border-b-0 hover:bg-parchment-100 disabled:opacity-50 flex items-center justify-between"
                                >
                                  <span>
                                    <strong className="font-medium">{w.text}</strong>
                                    {w.transliteration && (
                                      <span className="text-ink-400"> ({w.transliteration})</span>
                                    )}
                                    {meaningOf(w) && (
                                      <span className="text-ink-400"> — {meaningOf(w)}</span>
                                    )}
                                  </span>
                                  <span className="text-xs text-ink-400 shrink-0 ml-2">
                                    {inBank ? "In bank" : "Add"}
                                  </span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </>
                    )}
                    {!searching && matches.length === 0 && (
                      <p className="text-xs text-ink-400">
                        No match found — add it as a new word below.
                      </p>
                    )}
                  </div>
                )}

                {/* New word fields */}
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-ink-500 mb-1">Type</label>
                    <select
                      value={pos}
                      onChange={(e) => setPos(e.target.value)}
                      disabled={limitReached}
                      className="w-full px-2.5 py-2 rounded-lg border border-parchment-300 bg-white text-sm text-ink-800 disabled:opacity-60"
                    >
                      {POS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-ink-500 mb-1">
                      Transliteration (optional)
                    </label>
                    <input
                      type="text"
                      value={transliteration}
                      onChange={(e) => setTransliteration(e.target.value)}
                      disabled={limitReached}
                      className="w-full px-2.5 py-2 rounded-lg border border-parchment-300 bg-white text-sm text-ink-800 disabled:opacity-60"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-ink-500 mb-1">
                      Meaning / definition (optional)
                    </label>
                    <input
                      type="text"
                      value={definition}
                      onChange={(e) => setDefinition(e.target.value)}
                      disabled={limitReached}
                      placeholder="English meaning"
                      className="w-full px-2.5 py-2 rounded-lg border border-parchment-300 bg-white text-sm text-ink-800 disabled:opacity-60"
                    />
                  </div>
                </div>

                <button
                  onClick={handleAddNew}
                  disabled={adding || !text.trim() || limitReached}
                  className="mt-3 w-full px-3 py-2 rounded-lg bg-ink-500 hover:bg-ink-600 text-white text-sm font-semibold disabled:opacity-50"
                >
                  {adding ? "Adding…" : "Add new word"}
                </button>

                {limitReached && (
                  <p className="text-xs text-amber-700 mt-2">
                    You've reached your daily limit of {usage.limit} words. Try again tomorrow.
                  </p>
                )}
                {addError && <p className="text-xs text-red-600 mt-2">{addError}</p>}
                {addMsg && <p className="text-xs text-emerald-700 mt-2">{addMsg}</p>}
              </div>

              {/* ── Saved words, grouped by part of speech ── */}
              {loading && <p className="text-sm text-ink-400">Loading…</p>}
              {error && <p className="text-sm text-red-600">{error}</p>}
              {!loading && !error && entries.length === 0 && (
                <p className="text-sm text-ink-400">
                  Your Word Bank is empty. Add a word above to get started.
                </p>
              )}
              {!loading &&
                sections.map((section) => (
                  <div key={section.label} className="mb-4">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-500 mb-2">
                      {section.label}
                      <span className="text-ink-300"> ({section.items.length})</span>
                    </h4>
                    <ul className="list-none p-0 space-y-1.5">
                      {section.items.map((w) => {
                        const entry = entryByWordId[w.id];
                        const badge = STATUS_BADGE[entry?.status];
                        const noDefinition = !meaningOf(w);
                        // Safe for non-verbs: verbDisplayText falls back to w.text
                        // unless the word actually carries verb forms / a starred form.
                        const shownText = verbDisplayText(w);
                        return (
                          <li
                            key={w.id}
                            className="surface-glass surface--interactive flex items-center justify-between gap-2 px-3 py-2 rounded-lg"
                          >
                            <button
                              onClick={() => openDetail(w.id)}
                              className="text-left flex-1 min-w-0 flex items-center gap-3"
                            >
                              <span dir="rtl" className="font-arabic text-xl text-ink-800 min-w-[78px]">
                                {shownText}
                              </span>
                              <span className="flex flex-col flex-1 min-w-0">
                                {w.transliteration && (
                                  <span className="text-xs text-ink-400 italic truncate">
                                    {w.transliteration}
                                  </span>
                                )}
                                {meaningOf(w) && (
                                  <span className="text-sm text-ink-700 truncate">
                                    {meaningOf(w)}
                                  </span>
                                )}
                              </span>
                            </button>
                            {badge && (
                              <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${badge.cls}`}>
                                {badge.label}
                              </span>
                            )}
                            {noDefinition && (
                              entry?.definition_requested ? (
                                <span
                                  className="text-[10px] px-2 py-0.5 rounded-full bg-dt-gold-bg text-dt-gold-bright shrink-0"
                                  title="An admin will add a definition for this word"
                                >
                                  Definition requested
                                </span>
                              ) : (
                                <button
                                  onClick={() => entry && handleRequestDefinition(entry)}
                                  disabled={!entry || requestingId === entry?.id}
                                  className="text-[10px] px-2 py-0.5 rounded-full border border-violet-300 text-violet-700 hover:bg-violet-50 shrink-0 disabled:opacity-50"
                                  title="Ask an admin to add a definition for this word"
                                >
                                  {requestingId === entry?.id ? "Requesting…" : "Request definition"}
                                </button>
                              )
                            )}
                            {entry?.is_known && (
                              <>
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-ink-50 text-ink-700 shrink-0">
                                  Learnt
                                </span>
                                <button
                                  onClick={() => handleRelearn(w)}
                                  className="text-[10px] px-2 py-0.5 rounded-full border border-ink-200 text-ink-600 hover:bg-ink-50 shrink-0"
                                  title="Practice this word again"
                                >
                                  Re-learn
                                </button>
                              </>
                            )}
                            {isAudioPlaybackAllowed() && (
                            <button
                              onClick={() => playWord(w)}
                              className="p-1.5 rounded text-ink-400 hover:text-ink-700 hover:bg-parchment-100 shrink-0"
                              aria-label="Play"
                              title="Play"
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </button>
                            )}
                            <button
                              onClick={() => entry && handleRemove(entry)}
                              className="p-1.5 rounded text-ink-300 hover:text-red-600 hover:bg-red-50 shrink-0"
                              aria-label="Remove"
                              title="Remove from Word Bank"
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
            </>
          ) : mode === "learn" ? (
            // ── Learn mode: reuse the guided journey's step-1 component ──
            <>
              {loading && <p className="text-sm text-ink-400">Loading…</p>}
              {!loading && entries.length === 0 && (
                <p className="text-sm text-ink-400">
                  Add some words first, then come back to learn them.
                </p>
              )}
              {!loading && entries.length > 0 && (
                <VocabularyStep
                  sections={sections}
                  knownIds={knownIds}
                  reviewItemByWordId={reviewMap}
                  verbFormsByWordId={verbFormsMap}
                  particlesByWordId={particlesMap}
                  selectedWordId={detailWord?.id || null}
                  onWordSelect={(id) => openDetail(id)}
                  onPlayWord={(w) => playWord(w)}
                  onBatchComplete={load}
                  onAllLearnt={() => {}}
                  completed={false}
                  stepLabel="Word Bank"
                />
              )}

              {/* Already-learnt words — re-learn to practise them again */}
              {!loading && learntEntries.length > 0 && (
                <div className="mt-5 pt-4 border-t border-parchment-200">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-500 mb-2">
                    Learnt words ({learntEntries.length})
                    <span className="font-normal text-ink-300"> — re-learn to practise again</span>
                  </h4>
                  <ul className="list-none p-0 flex flex-wrap gap-2">
                    {learntEntries.map((e) => (
                      <li key={e.id}>
                        <button
                          onClick={() => handleRelearn(e.word)}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-ink-200 bg-white text-ink-700 hover:bg-ink-50 text-sm"
                          title="Move back into the quiz queue"
                        >
                          <span>{e.word.text}</span>
                          <span className="text-xs text-ink-400">↻ re-learn</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            // ── Sentences mode: ask the AI how to say something in Arabic ──
            // The whole Ask-AI form is hidden for users without app access
            // (e.g. a seatless institute admin) — the flag mirrors /me
            // has_access, same as the audio buttons; the endpoint 403s anyway.
            <>
              {isAudioPlaybackAllowed() && (
              <div className="surface-glass mb-5 p-4 rounded-xl">
                <h3 className="text-sm font-semibold text-ink-700 mb-1">Say it in Arabic</h3>
                <p className="text-xs text-ink-400 mb-2">
                  Type what you want to say in English — the AI suggests up to 3 ways
                  to say it. Tap one to save it below.
                </p>
                {usage && usage.askLimit != null && !isAdmin && (
                  <p className="text-xs text-ink-400 mb-2">
                    {Math.max(usage.askLimit - usage.askCount, 0)} of {usage.askLimit} daily
                    asks remaining.
                  </p>
                )}
                <textarea
                  value={askText}
                  onChange={(e) => {
                    setAskText(e.target.value);
                    setAskError(null);
                  }}
                  rows={2}
                  disabled={askLimitReached}
                  placeholder="e.g. How do I ask for the bill at a restaurant?"
                  className="w-full px-3 py-2 rounded-lg border border-parchment-300 bg-white text-sm text-ink-800 placeholder:text-ink-300 focus:outline-none focus:ring-2 focus:ring-ink-400 disabled:opacity-60"
                />
                <button
                  onClick={handleAsk}
                  disabled={asking || !askText.trim() || askLimitReached}
                  className="mt-2 w-full px-3 py-2 rounded-lg bg-ink-500 hover:bg-ink-600 text-white text-sm font-semibold disabled:opacity-50"
                >
                  {asking ? "Asking…" : "Ask AI"}
                </button>
                {askLimitReached && (
                  <p className="text-xs text-amber-700 mt-2">
                    You've reached your daily limit of {usage.askLimit} asks. Try again tomorrow.
                  </p>
                )}
                {askError && <p className="text-xs text-red-600 mt-2">{askError}</p>}

                {suggestions.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-ink-500 mb-1">Tap a sentence to save it:</p>
                    <ul className="list-none p-0 space-y-1.5">
                      {suggestions.map((s, i) => {
                        const saved = savedSentenceKeys.has(sentenceKey(s));
                        return (
                          <li key={i}>
                            <button
                              onClick={() => handleSaveSentence(s)}
                              disabled={saved}
                              className="surface-glass surface--interactive w-full text-left rounded-lg px-3 py-2 disabled:opacity-60"
                            >
                              <span className="block text-base text-ink-800" dir="rtl">{s.arabic}</span>
                              {s.translation && (
                                <span className="block text-xs text-ink-400 mt-0.5">{s.translation}</span>
                              )}
                              <span className="block text-[10px] text-emerald-600 mt-1">
                                {saved ? "✓ Saved" : "Tap to save"}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
              )}

              {/* Saved sentences */}
              <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-500 mb-2">
                Saved sentences
                <span className="text-ink-300"> ({sentences.length})</span>
              </h4>
              {sentences.length === 0 ? (
                <p className="text-sm text-ink-400">
                  No saved sentences yet. Ask the AI above and tap a result to save it.
                </p>
              ) : (
                <ul className="list-none p-0 space-y-1.5">
                  {sentences.map((s) => (
                    <li
                      key={s.id}
                      className="surface-glass flex items-start justify-between gap-2 px-3 py-2 rounded-lg"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-base text-ink-800 leading-loose" dir="rtl">
                          {s.arabic.split(/\s+/).filter(Boolean).map((tok, i) => (
                            <React.Fragment key={i}>
                              <button
                                onClick={() => handleTokenClick(tok)}
                                className="rounded px-0.5 hover:bg-dt-gold-bg hover:text-dt-gold-bright"
                                title="Add this word to My Words"
                              >
                                {tok}
                              </button>{" "}
                            </React.Fragment>
                          ))}
                        </p>
                        {s.translation && (
                          <p className="text-xs text-ink-400 mt-0.5">{s.translation}</p>
                        )}
                        <p className="text-[10px] text-ink-300 mt-0.5">Tap a word to add it to My Words</p>
                      </div>
                      {isAudioPlaybackAllowed() && (
                      <button
                        onClick={() => handlePlaySentence(s)}
                        className="p-1.5 rounded text-ink-400 hover:text-ink-700 hover:bg-parchment-100 shrink-0"
                        aria-label="Play"
                        title="Play"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </button>
                      )}
                      <button
                        onClick={() => handleDeleteSentence(s)}
                        className="p-1.5 rounded text-ink-300 hover:text-red-600 hover:bg-red-50 shrink-0"
                        aria-label="Delete sentence"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>

      {/* Word-detail overlay (layered above the bank) */}
      {(detailWord || detailLoading || detailError) && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-[3px] p-4"
          onClick={() => setDetailWord(null)}
        >
          <div
            className="surface-glass dark-scope rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto scroll-elegant"
            onClick={(e) => e.stopPropagation()}
          >
            <WordDetail
              word={detailWord}
              loading={detailLoading}
              error={detailError}
              onClear={() => setDetailWord(null)}
              topicsForWord={[]}
              topicsForWordLoading={false}
              topicsForWordError={null}
              onTopicSelect={() => {}}
              onFormWordClick={(wordId) => openDetail(wordId)}
              isAdmin={isAdmin}
              onWordUpdate={(updated) => setDetailWord(updated)}
            />
          </div>
        </div>
      )}

      {/* Add-a-word-from-sentence modal */}
      {tokenModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-[3px] p-4"
          onClick={() => setTokenModal(null)}
        >
          <div
            className="surface-glass dark-scope rounded-2xl shadow-2xl w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-ink-800">Add to My Words</h3>
              <button
                onClick={() => setTokenModal(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                aria-label="Close"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-2xl font-medium text-ink-800 text-center" dir="rtl">
                {tokenModal.token}
              </p>
              <button
                onClick={handleTokenAi}
                disabled={tokenAiBusy || tokenBusy}
                className="w-full px-3 py-1.5 rounded-lg border border-violet-300 text-violet-700 hover:bg-violet-50 text-sm font-medium disabled:opacity-50"
              >
                {tokenAiBusy ? "Looking up…" : "Auto-fill with AI"}
              </button>
              <div>
                <label className="block text-xs text-ink-500 mb-1">Type</label>
                <select
                  value={tokenPos}
                  onChange={(e) => setTokenPos(e.target.value)}
                  className="w-full px-2.5 py-2 rounded-lg border border-parchment-300 bg-white text-sm text-ink-800"
                >
                  {POS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-ink-500 mb-1">Transliteration (optional)</label>
                <input
                  type="text"
                  value={tokenTranslit}
                  onChange={(e) => setTokenTranslit(e.target.value)}
                  className="w-full px-2.5 py-2 rounded-lg border border-parchment-300 bg-white text-sm text-ink-800"
                />
              </div>
              <div>
                <label className="block text-xs text-ink-500 mb-1">Meaning / definition (optional)</label>
                <input
                  type="text"
                  value={tokenDef}
                  onChange={(e) => setTokenDef(e.target.value)}
                  placeholder="English meaning"
                  className="w-full px-2.5 py-2 rounded-lg border border-parchment-300 bg-white text-sm text-ink-800"
                />
              </div>
              {tokenError && <p className="text-xs text-red-600">{tokenError}</p>}
              <button
                onClick={handleTokenAdd}
                disabled={tokenBusy || tokenAiBusy}
                className="w-full px-3 py-2 rounded-lg bg-ink-500 hover:bg-ink-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                {tokenBusy ? "Adding…" : "Add to My Words"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Limit-reached notice */}
      {tokenNotice && (
        <div className="fixed inset-x-0 bottom-6 z-[70] flex justify-center px-4">
          <div className="flex items-center gap-3 bg-ink-800 text-white text-sm rounded-xl shadow-lg px-4 py-2.5 max-w-md">
            <span>{tokenNotice}</span>
            <button
              onClick={() => setTokenNotice(null)}
              className="text-white/70 hover:text-white shrink-0"
              aria-label="Dismiss"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
